import { useState } from "react";
import { Link } from "react-router-dom";
import { ShoppingBag, Check, Heart } from "lucide-react";
import { useAccount } from "../../store/AccountContext";
import { useCart, imgUrl, inr, pctOff } from "../../store/CartContext";
import { StarDisplay } from "./Stars";

export function WishButton({ id, className = "" }) {
  const { wishlist, toggleWish } = useAccount();
  const on = wishlist.includes(id);
  return (
    <button onClick={(e) => { e.preventDefault(); toggleWish(id); }} aria-label={on ? "Remove from wishlist" : "Add to wishlist"} data-testid={`wishlist-toggle-${id}`}
      className={`rounded-full p-2 transition-colors ${on ? "text-[#FC5106]" : "text-[#3915AA]/60 hover:text-[#FC5106]"} ${className}`}>
      <Heart className={`h-4 w-4 ${on ? "fill-[#FC5106]" : ""}`} />
    </button>
  );
}

export default function ProductCard({ product }) {
  const { add, items } = useCart();
  const firstInStock = product.variants.find((v) => v.in_stock) || product.variants[0];
  const [variant, setVariant] = useState(firstInStock);
  const off = pctOff(variant.price, variant.mrp);
  const inCart = items.some((i) => i.key === `${product.id}:${variant.id}`);
  const allOut = !product.variants.some((v) => v.in_stock);

  return (
    <div data-testid={`product-card-${product.id}`} className="group flex flex-col overflow-hidden rounded-2xl border border-[#3915AA]/10 bg-white transition-[box-shadow,transform] duration-300 hover:-translate-y-1 hover:shadow-[0_20px_40px_-20px_rgba(57,21,170,0.35)]">
      <Link to={`/shop/p/${product.slug}`} className="relative block aspect-square overflow-hidden bg-[#F7F5FF]">
        <img src={imgUrl(product.images?.[0])} alt={product.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        {off > 0 && <span className="absolute left-3 top-3 rounded-md bg-[#FC5106] px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-white">{off}% off</span>}
        {product.tags?.includes("bestseller") && <span className="absolute bottom-3 left-3 rounded-md bg-[#3915AA] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">Bestseller</span>}
        <WishButton id={product.id} className="absolute right-2 top-2 bg-white/90 shadow" />
        {allOut && <span className="absolute inset-0 flex items-center justify-center bg-white/70 text-sm font-bold uppercase tracking-wider text-[#1E0A5C]">Out of stock</span>}
      </Link>
      <div className="flex flex-1 flex-col p-3.5 sm:p-4">
        <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#3915AA]/60">{product.brand}</p>
        <Link to={`/shop/p/${product.slug}`} data-testid={`product-card-title-${product.id}`} className="mt-1 line-clamp-2 min-h-[2.5rem] font-heading text-sm font-bold leading-snug text-[#1E0A5C] hover:text-[#3915AA] sm:text-base">
          {product.name}
        </Link>
        {product.rating_count > 0 && <div className="mt-1"><StarDisplay value={product.rating_avg} count={product.rating_count} size="h-3 w-3" testid={`product-card-rating-${product.id}`} /></div>}
        {product.variants.length > 1 && (
          <div className="mt-2.5 flex flex-wrap gap-1.5">
            {product.variants.map((v) => (
              <button key={v.id} onClick={() => setVariant(v)} disabled={!v.in_stock} data-testid={`product-card-variant-${v.id}`}
                className={`rounded-md border px-2 py-0.5 text-[11px] font-semibold transition-colors ${variant.id === v.id ? "border-[#3915AA] bg-[#3915AA] text-white" : "border-[#3915AA]/20 text-[#1E0A5C] hover:border-[#3915AA]/50"} disabled:cursor-not-allowed disabled:opacity-40 disabled:line-through`}>
                {v.label}
              </button>
            ))}
          </div>
        )}
        <div className="mt-auto flex items-end justify-between gap-2 pt-3">
          <div data-testid={`product-card-price-${product.id}`}>
            <p className="font-heading text-lg font-bold leading-none text-[#1E0A5C]">{inr(variant.price)}</p>
            {off > 0 && <p className="mt-1 text-xs text-gray-400 line-through">{inr(variant.mrp)}</p>}
          </div>
          <button onClick={() => add(product, variant)} disabled={!variant.in_stock} data-testid={`product-card-add-to-cart-${product.id}`}
            className="inline-flex items-center gap-1.5 rounded-full bg-[#FC5106] px-3.5 py-2 text-xs font-bold text-white transition-colors hover:bg-[#E24804] disabled:bg-gray-300">
            {!variant.in_stock ? "Sold out" : inCart ? <><Check className="h-3.5 w-3.5" /> Added</> : <><ShoppingBag className="h-3.5 w-3.5" /> Add</>}
          </button>
        </div>
      </div>
    </div>
  );
}
